import {Barrier} from "@/modules/barriers/interfaces/barrier.interface";
import store from '@/store/store';
import {bus} from "@/plugins/bus";
import {IAuth} from "@/modules/Auth/interfaces/auth.interface";
import {IAppState} from "@/interfaces/app-state.interface";
import {IApp} from "@/interfaces/app.interface";
import {cookieBannerHelper} from "@/shared/CookieBanner/helpers/cookie-banner-helper";
import moment from "moment/moment";
import {httpResource} from "@/resources/http.resource";

export interface IGateActionItem {
  type?: 'action' | 'link' | undefined;
  icon: string;
  label: string;
  displayCondition?: boolean;
  href?: string;
  actionClick?: Function;
  class?: any[];
}

export class GateActionsHelper {
  getActionList(gate: Barrier.Entity): IGateActionItem[] {
    let userAccess: IAuth.UserAccess = (store.state as IAppState).auth.userAccess;
    let resultList: IGateActionItem[] = [
      {
        type: 'link',
        icon: 'camera',
        label: 'Камера',
        displayCondition: !!gate.urlVideo,
        href: gate.urlVideo,
      }, {
        icon: 'gear',
        label: 'Имитация подъезда',
        displayCondition: !!gate.scDemoMovs,
        actionClick: () => this.openDemMoves(gate)
      }, {
        icon: 'ruble-sign',
        label: 'Оплата наличными',
        displayCondition: userAccess && userAccess.scPayCashFlag && gate.scPayCashFlag,
        actionClick: () => this.openCashPay(gate)
      }, {
        icon: 'list',
        label: 'Журнал событий',
        displayCondition: userAccess && userAccess.scGateEventsLog,
        actionClick: () => this.openBarrierLog(gate)
      }, {
        icon: 'trailer',
        label: 'Прицеп',
        displayCondition: gate.scTrailer && !gate.trailerPriority,
        actionClick: () => this.openTrailer(gate)
      }, {
        icon: 'comment-dollar',
        label: 'Расшифровка суммы',
        displayCondition: gate.mode !== Barrier.CarNumberMode.NoCar,
        actionClick: () => this.decodeMoney(gate)
      }, {
        icon: 'magnifying-glass',
        label: 'Поиск заезда',
        displayCondition: userAccess && userAccess.scFindMovs,
        actionClick: () => this.openEventsReport(gate)
      }, {
        icon: 'app-barrier--open',
        label: `${gate.mode !== Barrier.CarNumberMode.HoldOpenGate ? 'Держать' : 'Снять удержание'} ШБ открытым`,
        displayCondition: userAccess && userAccess.scHoldOpenGateFlag && gate.scHoldOpenGateFlag,
        actionClick: () => this.holdOpenGate(gate)
      }, {
        icon: 'ban',
        label: `${gate.enterBlockedFlag ? 'Открыть' : 'Закрыть'} проезд`,
        displayCondition: gate.scEnterBlockedFlag,
        class: [{'color--red': gate.enterBlockedFlag}],
        actionClick: () => this.closeBarrier(gate)
      },
    ];

    return resultList;
  }

  async baseCloseAction() {
    bus.$emit(Barrier.BusEvents.RestartLoadBarriers);
  }

  async openDemMoves(gate: Barrier.Entity) {
    bus.$emit(Barrier.BusEvents.StopLoadingBarriers);
    await store.dispatch('dialog/openDialog', {
      blockName: 'barriers',
      dialogName: 'demoMoves',
      payload: {
        cameraId: gate.camId,
        closeAction: this.baseCloseAction,
      }
    });
  }

  async openCashPay(gate: Barrier.Entity) {
    bus.$emit(Barrier.BusEvents.StopLoadingBarriers);
    await store.dispatch('dialog/openDialog', {
      blockName: 'allPages',
      dialogName: 'cashPayPopup',
      payload: {
        formModel: {
          carNumber: gate.mode === Barrier.CarNumberMode.NoCar ? '' : gate.carNumber,
          tsum: gate.tSum,
          corpId: gate.corpId,
          parkId: gate.parkId,
        },
        hideParks: true,
        closeAction: this.baseCloseAction,
      }
    });
  }

  async openBarrierLog(gate: Barrier.Entity) {
    bus.$emit(Barrier.BusEvents.StopLoadingBarriers);
    await store.dispatch('dialog/openDialog', {
      blockName: 'barriers',
      dialogName: 'barrierLog',
      payload: {
        cameraId: gate.camId,
        gateName: gate.gateName,
        closeAction: this.baseCloseAction,
      }
    });
  }

  async openTrailer(gate: Barrier.Entity) {
    bus.$emit(Barrier.BusEvents.StopLoadingBarriers);

    await store.dispatch('dialog/openDialog', {
      blockName: 'barriers',
      dialogName: 'trailerPopup',
      payload: {
        gate: gate,
        closeAction: this.baseCloseAction,
      },
    });
  }

  async decodeMoney(gate: Barrier.Entity) {
    bus.$emit(Barrier.BusEvents.StopLoadingBarriers);

    await store.dispatch('dialog/openDialog', {
      blockName: 'allPages',
      dialogName: 'decodeMoneyPopup',
      payload: {
        movId: gate.movId,
        carNumber: gate.carNumber,
        closeAction: this.baseCloseAction,
      },
    });
  }

  async openEventsReport(gate: Barrier.Entity) {
    let confirmAction = async (formModel = null) => {
      let storageValue = localStorage.getItem(IApp.LocalStorageKeys.SelectParksGrouping);
      let groupingParks = storageValue && cookieBannerHelper.cookieConfig.optional ? JSON.parse(storageValue) : true;
      await store.dispatch('openPage', {
        routeName: IApp.ROUTE_NAME.PAGE_REPORT_EVENTS,
        params: {
          movId: gate.movId,
          filters: {
            str: formModel ? formModel.carNumber : gate.carNumber,
            parks: [groupingParks ? gate.globalParkId : gate.parkId],
            dateBeg: moment().add('days', -3).startOf('day').format(IApp.DateFormats.PrintedDate),
            dateEnd: moment().format(IApp.DateFormats.PrintedDate),
            searchFlag: true,
          },
        },
      });
    };

    if (gate.carId === -1) {
      await store.dispatch('dialog/openDialog', {
        blockName: 'allPages',
        dialogName: 'inputCarNumber',
        payload: {
          confirmAction,
        }
      });
    }
    else {
      await confirmAction();
    }
  }

  async closeBarrier(gate: Barrier.Entity) {
    let asyncAction = async () => {
      const action = Barrier.Action.GateMake;
      const params: any = {
        command: Barrier.Commands.EnterBlock,
        cameraId: gate.camId
      };
      let success = await httpResource.httpAction({ action, params });
      if (success) {
        bus.$notify({
          title: 'Успешно',
          message: gate.enterBlockedFlag ? 'Проезд открыт' : 'Проезд временно заблокирован',
          type: 'success'
        });
      }
    };
    await store.dispatch('defaultResourceAction', { asyncAction });
  }

  async holdOpenGate(gate: Barrier.Entity) {
    let asyncAction = async () => {
      const action = Barrier.Action.GateMake;
      const params: any = {
        command: Barrier.Commands.HoldOpenGate,
        cameraId: gate.camId
      };
      let success = await httpResource.httpAction({ action, params });
      if (success) {
        bus.$notify({
          title: 'Успешно',
          message: gate.mode !== Barrier.CarNumberMode.HoldOpenGate ? 'ШБ будет удерживаться открытым' : 'Удержание ШБ открытым отключено',
          type: 'success'
        });
      }
    };
    await store.dispatch('defaultResourceAction', { asyncAction });
  }
}

export const gateActionsHelper = new GateActionsHelper();